import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layout/Layout';

interface PromoCode {
  id: number;
  code: string;
  coin_reward: number;
  is_reusable: boolean;
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  is_active: boolean;
  description: string | null;
  is_expired: boolean;
  is_maxed_out: boolean;
  usage_count: number;
  unique_users: number;
  created_at: string;
}

const PromoCodesPage: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    coin_reward: 50,
    is_reusable: false,
    max_uses: '',
    expires_at: '',
    description: ''
  });

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/promo-codes');
      if (response.data.success) {
        setPromoCodes(response.data.data.promo_codes);
      }
    } catch (error) {
      console.error('Error loading promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.code || !formData.coin_reward) {
        alert('Code and coin reward are required');
        return;
      }

      if (editingId) {
        await axios.put(`/admin/promo-codes/${editingId}`, formData);
      } else {
        await axios.post('/admin/promo-codes', formData);
      }

      loadPromoCodes();
      setShowModal(false);
      resetForm();
      alert('Promo code saved successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save promo code');
    }
  };

  const handleEdit = (promo: PromoCode) => {
    setEditingId(promo.id);
    setFormData({
      code: promo.code,
      coin_reward: promo.coin_reward,
      is_reusable: promo.is_reusable,
      max_uses: promo.max_uses?.toString() || '',
      expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : '',
      description: promo.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) {
      return;
    }

    try {
      await axios.delete(`/admin/promo-codes/${id}`);
      loadPromoCodes();
      alert('Promo code deleted successfully');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete promo code');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      coin_reward: 50,
      is_reusable: false,
      max_uses: '',
      expires_at: '',
      description: ''
    });
    setEditingId(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Promo Codes</h4>
                <p className="text-muted mb-0">Create and manage promotional coin codes</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                <i className="bx bx-plus-circle me-2"></i>
                New Promo Code
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bx bx-gift font-size-48 text-muted mb-3"></i>
              <h5>No Promo Codes</h5>
              <p className="text-muted">Create your first promo code to reward users</p>
            </div>
          </div>
        ) : (
          <div className="row">
            {promoCodes.map((promo) => (
              <div key={promo.id} className="col-lg-4 col-md-6 mb-4">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h5 className="card-title mb-1 font-monospace">{promo.code}</h5>
                        <span className={`badge bg-${promo.is_active && !promo.is_expired && !promo.is_maxed_out ? 'success' : 'secondary'}`}>
                          {promo.is_expired ? 'Expired' : promo.is_maxed_out ? 'Max Uses' : promo.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="text-end">
                        <h4 className="mb-0 text-warning">{promo.coin_reward}</h4>
                        <small className="text-muted">coins</small>
                      </div>
                    </div>

                    {promo.description && (
                      <p className="text-muted small mb-3">{promo.description}</p>
                    )}

                    <div className="mb-2">
                      <small className="text-muted">Type:</small>{' '}
                      <span className="badge bg-info">{promo.is_reusable ? 'Reusable' : 'Single Use'}</span>
                    </div>

                    {promo.is_reusable && promo.max_uses && (
                      <div className="mb-2">
                        <small className="text-muted">Usage:</small>{' '}
                        <strong>{promo.current_uses} / {promo.max_uses}</strong>
                      </div>
                    )}

                    {!promo.is_reusable && (
                      <div className="mb-2">
                        <small className="text-muted">Used:</small>{' '}
                        <strong>{promo.current_uses > 0 ? 'Yes' : 'No'}</strong>
                      </div>
                    )}

                    {promo.expires_at && (
                      <div className="mb-3">
                        <small className="text-muted">Expires:</small>{' '}
                        {formatDate(promo.expires_at)}
                      </div>
                    )}

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(promo)}
                      >
                        <i className="bx bx-edit me-1"></i>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(promo.id)}
                      >
                        <i className="bx bx-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingId ? 'Edit' : 'Create'} Promo Code</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Promo Code *</label>
                    <input
                      type="text"
                      className="form-control font-monospace"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="WELCOME50"
                      disabled={!!editingId}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Coin Reward *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.coin_reward}
                      onChange={(e) => setFormData({ ...formData, coin_reward: parseInt(e.target.value) || 0 })}
                      min="1"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="New year promotion"
                    />
                  </div>

                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_reusable"
                      checked={formData.is_reusable}
                      onChange={(e) => setFormData({ ...formData, is_reusable: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="is_reusable">
                      Reusable Code
                    </label>
                  </div>

                  {formData.is_reusable && (
                    <div className="mb-3">
                      <label className="form-label">Max Uses (leave empty for unlimited)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.max_uses}
                        onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                        min="1"
                        placeholder="Unlimited"
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Expiry Date (optional)</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSave}>
                    {editingId ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </Layout>
  );
};

export default PromoCodesPage;

